<template>
    <div class="customer-view-container">
        <div v-if="customerdata" class="modern-customer-card">
            <!-- View Mode -->
            <div v-if="!edit">
                <div class="customer-header">
                    <div class="customer-avatar-section">
                        <div class="avatar-wrapper">
                            <div class="avatar-placeholder">
                                {{ getInitials(customerdata.customer_name) }}
                            </div>
                        </div>
                    </div>
                    
                    <div class="customer-info-section">
                        <h3 class="customer-name">{{ customerdata.customer_name || 'Unnamed Customer' }}</h3>
                        <div v-if="customerdata.company_name" class="customer-company">
                            {{ customerdata.company_name }}
                        </div>
                        
                        <!-- Phone Badge (Primary Identifier) -->
                        <div v-if="customerdata.customer_phone" class="primary-phone-badge">
                            <b-icon icon="telephone" class="mr-1"></b-icon>
                            {{ formatPhone(customerdata.customer_phone) }}
                        </div>
                        
                        <div class="customer-type-badge" :class="customerdata.customer_type || 'retail'">
                            {{ customerdata.customer_type === 'commercial' ? 'Commercial Customer' : 'Retail Customer' }}
                        </div>
                        
                        <div class="customer-stats-overview">
                            <div class="stat-item">
                                <div class="stat-value">{{ customerdata.total_orders || 0 }}</div>
                                <div class="stat-label">Total Orders</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ formatCurrency(customerdata.total_spent || 0) }}</div>
                                <div class="stat-label">Total Spent</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ formatCurrency(customerdata.avg_order_value || 0) }}</div>
                                <div class="stat-label">Avg Order Value</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">{{ formatDate(customerdata.last_order_date) }}</div>
                                <div class="stat-label">Last Order</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="customer-details-section">
                    <!-- Contact Information -->
                    <div class="detail-card">
                        <h5 class="detail-title">
                            <b-icon icon="person-lines-fill" class="mr-2"></b-icon>
                            Contact Information
                        </h5>
                        <div class="detail-grid">
                            <!-- Phone (Primary) -->
                            <div class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="telephone" class="mr-1"></b-icon>
                                    Phone (Primary)
                                </label>
                                <div class="detail-value">
                                    <a v-if="customerdata.customer_phone" :href="'tel:' + customerdata.customer_phone">
                                        {{ formatPhone(customerdata.customer_phone) }}
                                    </a>
                                    <span v-else>Not set</span>
                                </div>
                            </div>
                            
                            <!-- Email -->
                            <div class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="envelope" class="mr-1"></b-icon>
                                    Email
                                </label>
                                <div class="detail-value">
                                    <a v-if="customerdata.customer_email" :href="'mailto:' + customerdata.customer_email">
                                        {{ customerdata.customer_email }}
                                    </a>
                                    <span v-else>Not set</span>
                                </div>
                            </div>
                            
                            <!-- Full Name -->
                            <div class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="person" class="mr-1"></b-icon>
                                    Full Name
                                </label>
                                <div class="detail-value">{{ customerdata.customer_name || 'Not set' }}</div>
                            </div>
                            
                            <!-- Address -->
                            <div class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="geo-alt" class="mr-1"></b-icon>
                                    Address
                                </label>
                                <div class="detail-value">{{ customerdata.customer_address || 'Not set' }}</div>
                            </div>
                            
                            <!-- Customer ID (based on phone/email) -->
                            <div v-if="customerdata.customer_primary_id" class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="fingerprint" class="mr-1"></b-icon>
                                    Customer ID
                                </label>
                                <div class="detail-value">
                                    <code>{{ customerdata.customer_primary_id }}</code>
                                    <small class="text-muted ml-2">
                                        ({{ customerdata.customer_phone ? 'Phone-based' : 'Email-based' }})
                                    </small>
                                </div>
                            </div>
                            
                            <!-- Company Name -->
                            <div v-if="customerdata.company_name" class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="building" class="mr-1"></b-icon>
                                    Company Name
                                </label>
                                <div class="detail-value">{{ customerdata.company_name }}</div>
                            </div>
                            
                            <!-- Customer Type -->
                            <div class="detail-item">
                                <label class="detail-label">
                                    <b-icon icon="tag" class="mr-1"></b-icon>
                                    Customer Type
                                </label>
                                <div class="detail-value">
                                    <span class="type-tag" :class="customerdata.customer_type || 'retail'">
                                        {{ customerdata.customer_type === 'commercial' ? 'Commercial' : 'Retail' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Additional Information -->
                    <div class="detail-card">
                        <h5 class="detail-title">
                            <b-icon icon="info-circle" class="mr-2"></b-icon>
                            Additional Information
                        </h5>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <label class="detail-label">Customer Since</label>
                                <div class="detail-value">
                                    <b-icon icon="calendar" class="mr-2"></b-icon>
                                    {{ formatDate(customerdata.first_order_date) || 'No orders yet' }}
                                </div>
                            </div>
                            
                            <div class="detail-item">
                                <label class="detail-label">Preferred Location</label>
                                <div class="detail-value">
                                    <b-icon icon="geo-alt" class="mr-2"></b-icon>
                                    {{ getLocationNames() }}
                                </div>
                            </div>
                            
                            <!-- Order Type Preferences -->
                            <div class="detail-item">
                                <label class="detail-label">Order Preferences</label>
                                <div class="detail-value">
                                    <div v-if="customerdata.total_orders > 0" class="order-preferences">
                                        <div class="d-flex align-items-center">
                                            <b-icon 
                                                :icon="getOrderTypeIcon(customerdata.preferred_order_type)" 
                                                class="mr-2"
                                                :variant="getOrderTypeVariant(customerdata.preferred_order_type)"
                                            ></b-icon>
                                            <span>{{ formatOrderType(customerdata.preferred_order_type) }}</span>
                                        </div>
                                        <small class="text-muted d-block mt-1">
                                            {{ customerdata.store_pickup_orders || 0 }} store pickup · 
                                            {{ customerdata.home_delivery_orders || 0 }} home delivery
                                        </small>
                                    </div>
                                    <div v-else>No orders yet</div>
                                </div>
                            </div>
                            
                            <div class="detail-item">
                                <label class="detail-label">Notes</label>
                                <div class="detail-value">
                                    <pre class="customer-notes">{{ customerdata.notes || 'No notes' }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="action-buttons">
                    <b-button @click="triggerEdit" variant="primary" class="edit-btn">
                        <b-icon icon="pencil" class="mr-2"></b-icon>
                        Edit Customer
                    </b-button>
                    <b-button 
                        @click="viewCustomerOrders" 
                        variant="info" 
                        class="ml-2"
                    >
                        <b-icon icon="receipt" class="mr-2"></b-icon>
                        View Orders
                    </b-button>
                </div>
            </div>

            <!-- Edit Mode -->
            <div v-else>
                <div class="edit-header">
                    <h3 class="edit-title">
                        <b-icon icon="pencil" class="mr-2"></b-icon>
                        {{ customerdata.customer_primary_id ? 'Edit Customer' : 'Add New Customer' }}
                    </h3>
                    <p class="edit-subtitle text-muted" v-if="customerdata.customer_primary_id">
                        Customer ID: {{ customerdata.customer_primary_id }}
                    </p>
                </div>

                <div class="edit-form">
                    <b-card no-body class="mb-4">
                        <b-card-header class="bg-light">
                            <h6 class="mb-0">Contact Information</h6>
                            <small class="text-muted">Phone is used as the primary identifier</small>
                        </b-card-header>
                        <b-card-body>
                            <b-row>
                                <!-- Phone (Primary Field) -->
                                <b-col md="6">
                                    <b-form-group label="Phone *" label-for="phone" :state="phoneState">
                                        <b-form-input
                                            id="phone"
                                            v-model="customerdata.customer_phone"
                                            placeholder="(123) 456-7890"
                                            required
                                            :state="phoneState"
                                        ></b-form-input>
                                        <b-form-text>
                                            Primary identifier for customer records
                                        </b-form-text>
                                        <b-form-invalid-feedback>
                                            Phone number is required for customer identification
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                
                                <!-- Email (Secondary) -->
                                <b-col md="6">
                                    <b-form-group label="Email" label-for="email">
                                        <b-form-input
                                            id="email"
                                            v-model="customerdata.customer_email"
                                            type="email"
                                            placeholder="customer@example.com"
                                        ></b-form-input>
                                        <b-form-text>
                                            Used as fallback identifier if phone is not available
                                        </b-form-text>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            
                            <b-row>
                                <!-- Full Name -->
                                <b-col md="6">
                                    <b-form-group label="Full Name *" label-for="fullname" :state="nameState">
                                        <b-form-input
                                            id="fullname"
                                            v-model="customerdata.customer_name"
                                            placeholder="Enter customer full name"
                                            required
                                            :state="nameState"
                                        ></b-form-input>
                                        <b-form-invalid-feedback>
                                            Customer name is required
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </b-col>
                                
                                <!-- Customer Type -->
                                <b-col md="6">
                                    <b-form-group label="Customer Type" label-for="customer_type">
                                        <b-form-select
                                            id="customer_type"
                                            v-model="customerdata.customer_type"
                                            :options="customer_type_options"
                                        ></b-form-select>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-card>

                    <b-card no-body class="mb-4">
                        <b-card-header class="bg-light">
                            <h6 class="mb-0">Additional Information</h6>
                        </b-card-header>
                        <b-card-body>
                            <b-row>
                                <!-- Address -->
                                <b-col md="12">
                                    <b-form-group label="Address" label-for="address">
                                        <b-form-textarea
                                            id="address"
                                            v-model="customerdata.customer_address"
                                            placeholder="Enter full address"
                                            rows="2"
                                        ></b-form-textarea>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            
                            <b-row>
                                <!-- Company Name -->
                                <b-col md="6">
                                    <b-form-group label="Company Name" label-for="company_name">
                                        <b-form-input
                                            id="company_name"
                                            v-model="customerdata.company_name"
                                            placeholder="Company name (if applicable)"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                
                                <!-- Preferred Locations -->
                                <b-col md="6">
                                    <b-form-group label="Preferred Locations" label-for="location_ids">
                                        <b-form-select
                                            id="location_ids"
                                            v-model="selectedLocations"
                                            :options="locations_data"
                                            multiple
                                            :select-size="4"
                                        ></b-form-select>
                                        <small class="text-muted">Hold Ctrl/Cmd to select multiple locations</small>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            
                            <b-row>
                                <!-- Notes -->
                                <b-col md="12">
                                    <b-form-group label="Notes" label-for="notes">
                                        <b-form-textarea
                                            id="notes"
                                            v-model="customerdata.notes"
                                            placeholder="Additional notes about this customer"
                                            rows="3"
                                        ></b-form-textarea>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-card>

                    <!-- Action Buttons -->
                    <div class="edit-actions">
                        <b-button 
                            @click="cancelEdit" 
                            variant="outline-secondary" 
                            class="mr-2"
                        >
                            Cancel
                        </b-button>
                        
                        <b-button 
                            @click="customerdata.customer_primary_id ? updateCustomer($event) : addNew($event)" 
                            variant="primary"
                            :disabled="!isFormValid"
                        >
                            {{ customerdata.customer_primary_id ? 'Update Customer' : 'Save Customer' }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Requestor from "@/utilities/Requestor"

    export default {
        name: "CustomerView",
        props: ["viewdata", "new"],
        data: function() {
            return {
                packet: {},
                customerdata: {},
                selectedLocations: [],
                locations_data: [],
                customer_type_options: [
                    { value: 'retail', text: 'Retail' },
                    { value: 'commercial', text: 'Commercial' }
                ],
                edit: false
            };
        },
        computed: {
            phoneState() {
                return this.customerdata.customer_phone ? true : false;
            },
            nameState() {
                return this.customerdata.customer_name ? true : false;
            },
            isFormValid() {
                // At minimum, we need either phone or email, plus a name
                const hasContact = this.customerdata.customer_phone || this.customerdata.customer_email;
                const hasName = this.customerdata.customer_name;
                return hasContact && hasName;
            }
        },
        methods: {
            getInitials(name) {
                if (!name) return '?';
                return name.split(' ')
                    .map(function(word) { return word[0]; })
                    .join('')
                    .toUpperCase()
                    .substring(0, 2);
            },
            
            formatCurrency(value) {
                if (!value && value !== 0) return '$0.00';
                const num = parseFloat(value);
                if (isNaN(num)) return '$0.00';
                return '$' + num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            
            formatDate(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } catch (error) {
                    return dateString;
                }
            },
            
            formatPhone(phone) {
                if (!phone) return '';
                var cleaned = ('' + phone).replace(/\D/g, '');
                var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
                if (match) {
                    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
                }
                return phone;
            },
            
            formatOrderType(orderType) {
                const types = {
                    'store_pickup': 'Prefers Store Pickup',
                    'home_delivery': 'Prefers Home Delivery',
                    'both': 'Uses Both Types',
                    'unknown': 'No Orders Yet'
                };
                return types[orderType] || orderType;
            },
            
            getOrderTypeIcon(orderType) {
                const icons = {
                    'store_pickup': 'shop',
                    'home_delivery': 'truck',
                    'both': 'shuffle',
                    'unknown': 'question-circle'
                };
                return icons[orderType] || 'question-circle';
            },
            
            getOrderTypeVariant(orderType) {
                const variants = {
                    'store_pickup': 'success',
                    'home_delivery': 'warning',
                    'both': 'info',
                    'unknown': 'secondary'
                };
                return variants[orderType] || 'secondary';
            },
            
            getLocationNames() {
                if (!this.customerdata.location_ids || !this.packet.locationsdict) return 'N/A';
                const ids = this.customerdata.location_ids.split(',').map(Number);
                return ids.map(id => this.packet.locationsdict[id]).join(', ') || 'N/A';
            },
            
            viewCustomerOrders() {
                console.log("Emitting view-orders event:", this.customerdata);
                this.$emit('view-orders', this.customerdata);
            },
            
            // Edit Methods
            triggerEdit() {
                this.edit = true;
                // Parse location_ids string to array for multi-select
                if (this.customerdata.location_ids) {
                    this.selectedLocations = this.customerdata.location_ids.split(',').map(Number);
                } else {
                    this.selectedLocations = [];
                }
            },
            
            cancelEdit() {
                this.customerdata = JSON.parse(JSON.stringify(this.viewdata));
                this.edit = false;
                this.selectedLocations = [];
            },
            
            updateCustomer(e) {
                if (e) {
                    e.preventDefault();
                }
                
                // Validate form
                if (!this.customerdata.customer_name) {
                    this.$bvToast.toast(
                        "Customer name is required", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }
                
                if (!this.customerdata.customer_phone && !this.customerdata.customer_email) {
                    this.$bvToast.toast(
                        "At least one contact method (phone or email) is required", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }

                var that = this;
                var path = "/api/customers/update_customer";
                var customerdata = JSON.parse(JSON.stringify(this.customerdata));
                
                // Convert selected locations array back to comma-separated string
                if (this.selectedLocations && this.selectedLocations.length > 0) {
                    customerdata.location_ids = this.selectedLocations.join(',');
                } else {
                    customerdata.location_ids = null;
                }
                
                // Ensure we have the primary identifier
                if (!customerdata.customer_primary_id) {
                    customerdata.customer_primary_id = customerdata.customer_phone || customerdata.customer_email;
                }
                
                var data = { "customerdata": customerdata };
                
                var success = function(response) {
                    console.log("Update response:", response);
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error, 
                            { title: "Error", autoHideDelay: 1500, variant: "danger" }
                        );
                    } else {
                        that.$bvToast.toast(
                            "Customer updated successfully", 
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                        that.customerdata = response.data.result.customer || customerdata;
                        that.$emit('updated', that.customerdata);
                    }
                    that.edit = false;
                };
                
                var failure = function(error) {
                    console.error("Update error:", error);
                    that.$bvToast.toast(
                        "Error updating customer. Please try again.", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    that.edit = false;
                };
                
                Requestor.postSyncP(path, data, this, success, failure);
            },
            
            addNew(e) {
                e.preventDefault();
                
                // Validate form
                if (!this.customerdata.customer_name) {
                    this.$bvToast.toast(
                        "Customer name is required", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }
                
                if (!this.customerdata.customer_phone && !this.customerdata.customer_email) {
                    this.$bvToast.toast(
                        "At least one contact method (phone or email) is required", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }

                var that = this;
                var customerdata = JSON.parse(JSON.stringify(this.customerdata));
                
                // Convert selected locations array back to comma-separated string
                if (this.selectedLocations && this.selectedLocations.length > 0) {
                    customerdata.location_ids = this.selectedLocations.join(',');
                }
                
                // Set the primary identifier based on phone/email
                customerdata.customer_primary_id = customerdata.customer_phone || customerdata.customer_email;
                
                var path = "/api/customers/add_customer";
                var data = customerdata;
                
                var success = function(response) {
                    console.log("Add response:", response);
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error, 
                            { title: "Error", autoHideDelay: 1500, variant: "danger" }
                        );
                    } else {
                        that.customerdata = response.data.result.customer || customerdata;
                        that.edit = false;
                        that.selectedLocations = [];

                        that.$bvToast.toast(
                            "Customer added successfully.", 
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                        that.$emit('added', that.customerdata);
                    }
                };

                var failure = function(error) {
                    console.error("Add error:", error);
                    that.$bvToast.toast(
                        "Error adding new customer. Please try again.", 
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                };
                
                Requestor.postSyncP(path, data, this, success, failure);
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            this.customerdata = JSON.parse(JSON.stringify(this.viewdata));
            
            // Prepare locations data
            if (this.packet.locations) {
                this.locations_data = this.packet.locations.filter(function(d) {
                    return d.text !== "All";
                }).map(function(loc) {
                    return { value: loc.id || loc.value, text: loc.text || loc.name };
                });
            }
            
            this.new = this.new || false;
            if (this.new) {
                this.edit = true;
            }
        }
    };
</script>

<style scoped>
.customer-view-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.modern-customer-card {
    background: white;
    border-radius: 10px;
    padding: 0;
}

/* Header Section */
.customer-header {
    display: flex;
    align-items: center;
    padding: 25px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px 10px 0 0;
}

.customer-avatar-section {
    flex-shrink: 0;
    margin-right: 25px;
    text-align: center;
}

.avatar-wrapper {
    cursor: pointer;
    margin-bottom: 10px;
}

.avatar-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 2rem;
    border: 3px solid rgba(255, 255, 255, 0.3);
}

.customer-info-section {
    flex: 1;
}

.customer-name {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 5px;
}

.customer-company {
    font-size: 1.1rem;
    opacity: 0.9;
    margin-bottom: 10px;
    font-style: italic;
}

/* Phone Badge in Header */
.primary-phone-badge {
    display: inline-block;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 20px;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 15px;
    border: 2px solid rgba(255, 255, 255, 0.4);
}

.customer-type-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 20px;
}

.customer-type-badge.retail {
    background: rgba(66, 153, 225, 0.3);
}

.customer-type-badge.commercial {
    background: rgba(159, 122, 234, 0.3);
}

/* Customer Stats Overview */
.customer-stats-overview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.stat-item {
    text-align: center;
    padding: 15px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 5px;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Details Section */
.customer-details-section {
    padding: 25px;
}

.detail-card {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.detail-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
}

.detail-item {
    display: flex;
    flex-direction: column;
}

.detail-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #718096;
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-value {
    font-size: 1rem;
    color: #2d3748;
    font-weight: 500;
}

.detail-value a {
    color: #667eea;
    text-decoration: none;
}

.detail-value a:hover {
    text-decoration: underline;
}

.type-tag {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
}

.type-tag.retail {
    background: rgba(66, 153, 225, 0.15);
    color: #2c5282;
}

.type-tag.commercial {
    background: rgba(159, 122, 234, 0.15);
    color: #553c9a;
}

/* Customer ID Display */
.detail-value code {
    background: #f7fafc;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9rem;
}

/* Order Preferences Display */
.order-preferences {
    padding: 8px;
    background: #f7fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.customer-notes {
    white-space: pre-wrap;
    word-wrap: break-word;
    background: white;
    padding: 10px;
    border-radius: 5px;
    border: 1px solid #e2e8f0;
    font-family: inherit;
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;
}

/* Edit Mode */
.edit-header {
    padding: 25px 25px 0;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 20px;
}

.edit-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
}

.edit-subtitle {
    font-size: 0.9rem;
    margin-top: 5px;
}

.edit-form {
    padding: 25px;
}

/* Form Validation States */
.form-group.required label:after {
    content: " *";
    color: #f56565;
}

/* Action Buttons */
.action-buttons {
    padding: 0 25px 25px;
    text-align: right;
}

.edit-btn {
    padding: 10px 20px;
    font-weight: 600;
    border-radius: 8px;
}

.edit-actions {
    padding: 20px 0 0;
    text-align: right;
    border-top: 1px solid #e2e8f0;
}

/* Responsive */
@media (max-width: 768px) {
    .customer-header {
        flex-direction: column;
        text-align: center;
        padding: 20px;
    }
    
    .customer-avatar-section {
        margin-right: 0;
        margin-bottom: 20px;
    }
    
    .primary-phone-badge {
        font-size: 0.9rem;
        padding: 6px 12px;
    }
    
    .detail-grid {
        grid-template-columns: 1fr;
    }
    
    .customer-stats-overview {
        grid-template-columns: 1fr 1fr;
    }
    
    .action-buttons {
        text-align: center;
    }
    
    .action-buttons .btn {
        width: 100%;
        margin-bottom: 10px;
    }
    
    .edit-form .row > [class*="col-"] {
        margin-bottom: 15px;
    }
    
    .edit-form .card-body {
        padding: 15px;
    }
}

@media (max-width: 480px) {
    .customer-name {
        font-size: 1.5rem;
    }
    
    .customer-stats-overview {
        grid-template-columns: 1fr;
    }
    
    .detail-card {
        padding: 15px;
    }
    
    .edit-actions {
        text-align: center;
    }
    
    .edit-actions .btn {
        width: 100%;
        margin-bottom: 10px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/CustomerView.vue